// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { Info as InfoIcon } from '@/constants/hardfin-standard-icons';
import { closeFulfillmentOrder } from '@/fulfillment/api/close-fulfillment-order';
import { FulfillmentOrder } from '@/fulfillment/api/types';
import { useSupportError } from '@/hooks/use-support-error';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

const CANNOT_CLOSE_MESSAGE =
  'Only partially shipped or partially delivered orders that are not affiliated with a 3PL can be closed.';

interface Props {
  isOpen: boolean;
  fulfillmentKey: string;
  foNumber: string;
  onClose: () => void;
  onSuccess: (updatedFulfillment: FulfillmentOrder) => void;
}

export function FulfillmentCloseDialog(props: Props) {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { showSupportError, hideSupportError } = useSupportError();

  const closeMutation = useMutation({
    mutationFn: () => closeFulfillmentOrder(props.fulfillmentKey),
    onSuccess: (updatedFulfillment) => {
      close();
      props.onSuccess(updatedFulfillment);
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      setErrorMessage('');
      hideSupportError();
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.fulfillment_order_cannot_close
      ) {
        setErrorMessage(CANNOT_CLOSE_MESSAGE);
        return;
      }
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setErrorMessage(curated);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        showSupportError(
          'An error occurred while closing the fulfillment order.'
        );
      }
    },
  });

  const handleConfirm = () => {
    setErrorMessage('');
    hideSupportError();
    closeMutation.mutate();
  };

  const close = () => {
    props.onClose();
    setErrorMessage('');
  };

  const handleClose = (_event: object, reason: string) => {
    if (
      closeMutation.isPending &&
      (reason === 'backdropClick' || reason === 'escapeKeyDown')
    ) {
      return;
    }
    close();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
        }}
      >
        Close fulfillment order
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Alert
          severity="info"
          icon={<InfoIcon fontSize="medium" />}
          sx={{
            marginY: 3,
            alignItems: 'center',
          }}
        >
          <AlertTitle variant="h6">
            Close order <strong>{props.foNumber}</strong>?
          </AlertTitle>
          <Stack spacing={1}>
            <Typography variant="body1">
              Close an order when it should not be fulfilled further (no more
              shipments are desired or expected). Closing an order is permanent
              and cannot be undone.
            </Typography>
            <Typography variant="body1">{CANNOT_CLOSE_MESSAGE}</Typography>
          </Stack>
        </Alert>

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={close}
          variant="outlined"
          disabled={closeMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={closeMutation.isPending}
          sx={{ ml: 1.5 }}
        >
          {closeMutation.isPending ? 'Closing...' : 'Close order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
